/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Collapse } from '@chakra-ui/react';
import { ChevronDownIcon, TimeHourglassIcon } from '@sketchnote/icons';
import { Avatar, Switch } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import cn from 'classnames';
import * as React from 'react';
import { useBoardStore } from 'src/features/boards';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';

import { useTasksStore } from '../store/useTasksStore';
import { EstimatedTimeObject } from '../types/tasks';

const { useCallback, useState, useEffect } = React;

const NewTimeInput = ({
  value,
  onChange,
  disabled,
}: {
  value?: EstimatedTimeObject;
  onChange?: (time: {
    days: number;
    hours: number;
    minutes: number;
    text: string;
    member?: string | null;
  }) => void;
  disabled?: boolean;
}) => {
  const [member, setMember] = useState(value?.member);
  const [days, setDays] = useState(value?.estimate?.days ?? 0);
  const [hours, setHours] = useState(value?.estimate?.hours ?? 0);
  const [minutes, setMinutes] = useState(value?.estimate?.minutes ?? 0);
  const [inputText, setInputText] = useState(value?.estimate?.text || '0 days 0 hours 0 minutes');

  useEffect(() => {
    if (value?.type === 'total') {
      setDays(value?.estimate?.days ?? 0);
      setHours(value?.estimate?.hours ?? 0);
      setMinutes(value?.estimate?.minutes ?? 0);
      setInputText(value?.estimate?.text || '0 days 0 hours 0 minutes');
    }
  }, [value?.estimate?.text]);

  useEffect(() => {
    setMember(value?.member);
  }, [value?.member]);

  const handleInputChange = (event) => {
    const { value } = event.target;
    setInputText(value);
    const { parsedDays, parsedHours, parsedMinutes } = parseInput(value);
    onChange?.({
      days: parsedDays,
      hours: parsedHours,
      minutes: parsedMinutes,
      text: value,
      member,
    });
  };

  const parseInput = (inputText) => {
    // Use a regular expression to match numbers and units
    const regex = /(\d+)\s*d\s*|(\d+)\s*h\s*|(\d+)\s*m\s*/g;
    const matches = inputText.matchAll(regex);

    let parsedDays = 0;
    let parsedHours = 0;
    let parsedMinutes = 0;

    for (const match of matches) {
      if (match[1]) {
        parsedDays += parseInt(match[1], 10);
      }
      if (match[2]) {
        parsedHours += parseInt(match[2], 10);
      }
      if (match[3]) {
        parsedMinutes += parseInt(match[3], 10);
      }
    }

    setDays(parsedDays);
    setHours(parsedHours);
    setMinutes(parsedMinutes);

    return {
      parsedDays,
      parsedHours,
      parsedMinutes,
    };
  };

  return (
    <div className="flex flex-col">
      <div className="flex relative">
        <TimeHourglassIcon
          wrapperClass="absolute text-gray-400 p-5 top-8 left-10 left-12 border-2 border-gray-400 text-gray-400 border-dotted rounded-full"
          size={18}
        />
        <input
          type="text"
          name="inputText"
          value={inputText}
          onChange={handleInputChange}
          placeholder="Enter time (e.g. 3d 4h 30m)"
          disabled={disabled}
          className={cn(
            ' bg-white p-12 pl-50 w-full placeholder-gray-400 rounded-sm h-48 border-gray-200 text-left border bg-gray-100'
          )}
        />
      </div>
      <div>
        <span>{days} Days </span>
        <span>{hours} Hours </span>
        <span>{minutes} Minutes </span>
      </div>
    </div>
  );
};

export const EditEstimatedTime = ({
  members,
  placeholderText = '-',
}: {
  members?: any;
  placeholderText?: string;
}) => {
  const taskId = useTasksStore((s) => s.currentTaskId);
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [isIndividualEstimated, setIsIndividualEstimated] = useState<boolean>(false);
  const [estimatedTimeObject, setEstimatedTimeObject] = useState<EstimatedTimeObject[] | null>();
  const [memberCollapse, setMemberCollapse] = useState(true);
  const boardsStore = useBoardStore();
  const onUpdateItemField = useTasksStore((s) => s.onUpdateItemField);
  const item = useTasksStore(
    useCallback(
      (s) => {
        if (taskId) {
          return s.tasks[taskId];
        }
      },
      [taskId]
    )
  );

  const { isReadOnly } = useTaskAccess(taskId || '');

  useEffect(() => {
    // iterate through all the assignees, check if they do not have existing values
    // then set days, months, minutes to 0, and text to 0 days 0 hours 0 minutes
    if (isIndividualEstimated && item?.assignee && estimatedTimeObject) {
      const assignees = item?.assignee || [];
      const assigneeEstimates = estimatedTimeObject?.filter((item) => {
        if (item.type === 'member') {
          return true;
        }
        return false;
      });
      const assigneeEstimateMemberIds = assigneeEstimates?.map((item) => item.member);
      const assigneeWithNoEstimates = assignees?.filter(
        (assignee) => !assigneeEstimateMemberIds?.includes(assignee.memberId)
      );
      const assigneeEstimatesWithNoEstimates: EstimatedTimeObject[] = assigneeWithNoEstimates?.map(
        (assignee) => ({
          type: 'member',
          member: assignee.memberId,
          estimate: {
            days: 0,
            hours: 0,
            minutes: 0,
            text: '0 days 0 hours 0 minutes',
          },
        })
      );

      let totalEstimate: EstimatedTimeObject | undefined = estimatedTimeObject?.find(
        (item) => item.type === 'total'
      );
      if (!totalEstimate) {
        totalEstimate = {
          type: 'total',
          member: null,
          estimate: {
            days: 0,
            hours: 0,
            minutes: 0,
            text: '0 days 0 hours 0 minutes',
          },
        };
      }
      setEstimatedTimeObject([
        totalEstimate,
        ...assigneeEstimatesWithNoEstimates!,
        ...assigneeEstimates!,
      ]);
    }
  }, [isIndividualEstimated]);

  //for custom filed dependency
  // const assignees = members || [];
  const assignees = item?.assignee || [];

  const handleTotalEstimatedChange = ({
    days,
    hours,
    minutes,
    text,
    member,
  }: {
    days: number;
    hours: number;
    minutes: number;
    text: string;
    member?: string;
  }) => {
    const estimateObject: EstimatedTimeObject = {
      type: 'total',
      member: null,
      estimate: {
        days,
        hours,
        minutes,
        text,
      },
    };
    setEstimatedTimeObject([estimateObject]);
  };

  const addEstimateTimes = (...times) => {
    // Initialize variables to store the total time components
    let totalMinutes = 0;
    let totalHours = 0;
    let totalDays = 0;
    // Iterate through each time object and accumulate the components
    for (const time of times) {
      totalMinutes += time.minutes;
      totalHours += time.hours;
      totalDays += time.days;
    }

    // Handle carryover
    const carryMinutes = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;

    const carryHours = Math.floor(totalHours / 24);
    const remainingHours = totalHours % 24;

    // Calculate the final time object
    const resultTime = {
      days: totalDays + carryHours + carryMinutes,
      hours: remainingHours,
      minutes: remainingMinutes,
    };

    return {
      ...resultTime,
      text: `${resultTime.days} days ${resultTime.hours} hours ${resultTime.minutes} minutes`,
    };
  };

  const handleIndividualEstimate = ({
    days,
    hours,
    minutes,
    text,
    member,
  }: {
    days: number;
    hours: number;
    minutes: number;
    text: string;
    member?: string;
  }) => {
    const itemsToAdd: any = [];
    const assigneeEstimates = estimatedTimeObject?.filter((item) => {
      if (item.type === 'member' && item.member !== member) {
        itemsToAdd.push(item.estimate);
        return true;
      }
      return false;
    });

    const modifiedEstimate: EstimatedTimeObject = {
      type: 'member',
      member,
      estimate: { days, hours, minutes, text },
    };

    const totalEstimate = addEstimateTimes(...itemsToAdd, modifiedEstimate.estimate);

    setEstimatedTimeObject([
      {
        type: 'total',
        member: null,
        estimate: totalEstimate,
      },
      ...assigneeEstimates!,
      modifiedEstimate,
    ]);
  };

  const handleCloseEstimates = () => {
    setIsSelected(false);
    if (item?.id && estimatedTimeObject) {
      const estimates = isIndividualEstimated
        ? estimatedTimeObject
        : estimatedTimeObject.filter((item) => item.type === 'total');
      onUpdateItemField({
        item: { estimates, id: item.id },
        keys: ['estimates'],
      });
    } else if (item?.id && !estimatedTimeObject) {
      onUpdateItemField({
        item: { estimates: [], id: item.id },
        keys: ['estimates'],
      });
    }
  };

  useEffect(() => {
    if (item?.estimates) {
      setEstimatedTimeObject(item.estimates);
      const hasAssigneeEstimates = item.estimates.some((item) => item.type === 'member');
      setIsIndividualEstimated(hasAssigneeEstimates);
    }
  }, []);

  const showEsimatedTime = (estimateObject: EstimatedTimeObject[]) => {
    if (estimateObject) {
      const estimateValue = estimateObject?.find((i) => i.type === 'total')?.estimate;
      if (estimateValue) {
        let estimateStr = '';
        if (estimateValue.days > 0) {
          estimateStr += `${estimateValue.days} day${estimateValue.days > 1 ? 's' : ''} `;
        }
        if (estimateValue.hours > 0) {
          estimateStr += `${estimateValue.hours} hr${estimateValue.hours > 1 ? 's' : ''} `;
        }
        if (estimateValue.minutes > 0) {
          estimateStr += `${estimateValue.minutes} min${estimateValue.minutes > 1 ? 's' : ''}`;
        }

        return estimateStr;
      }
      return placeholderText;
    }
    return placeholderText;
  };

  if (!item) return <></>;

  return (
    <div className="relative">
      {isSelected && !isReadOnly && (
        <>
          <button
            className="fixed w-full h-full top-0 left-0 z-10 cursor-default"
            onClick={handleCloseEstimates}
          />
          <div className="rounded shadow-lg absolute  p-12 -right-200 bg-white w-364 z-10">
            <label htmlFor="totalEstimatedTime" className="text-base mb-14 block text-gray-500">
              Total Estimated time
            </label>
            <div className="relative mb-16">
              <NewTimeInput
                value={estimatedTimeObject?.find((i) => i.type === 'total')}
                onChange={handleTotalEstimatedChange}
                disabled={isIndividualEstimated}
              />
            </div>
            {assignees.length > 0 && (
              <div className="py-12 w-332 border-t flex items-center justify-between">
                <label htmlFor="individualEstimate" className="text-gray-500">
                  Set individual estimate
                </label>
                <Switch
                  className="mt-2"
                  checked={isIndividualEstimated}
                  onChange={() => setIsIndividualEstimated(!isIndividualEstimated)}
                />
              </div>
            )}
            <div>
              {isIndividualEstimated &&
                assignees.map((assignee) => (
                  <div key={assignee.memberId}>
                    <div className="flex items-center mb-16">
                      <Avatar src={assignee.avatar} type="photo" name={assignee.firstname} />
                      <span className="ml-8">{assignee.firstname}</span>
                    </div>
                    <div className="relative mb-16">
                      <NewTimeInput
                        value={estimatedTimeObject?.find((i) => i.member === assignee.memberId)}
                        onChange={handleIndividualEstimate}
                        disabled={false}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}
      <div className="flex flex-row items-center justify-between">
        {/* <label
          htmlFor="estimatedTime"
          className={cn(' text-base mb-14 block text-gray-999 font-medium', {})}
        >
          Estimated time
        </label>
        <span
          style={{
            transform: memberCollapse ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'all 0.5s',
          }}
          className={' ml-20 mr-6 block text-gray-500'}
          onClick={(e) => {
            setMemberCollapse(!memberCollapse);
          }}
        >
          <ChevronDownIcon />
        </span> */}
      </div>
      <div className="relative">
        <Tippy
          content="You do not have the required permission to edit tasks"
          disabled={!isReadOnly}
        >
          <div className="cursor-pointer text-center text-sm" onClick={() => setIsSelected(true)}>
            {showEsimatedTime(estimatedTimeObject!)}
          </div>
        </Tippy>
        {/* <input
          id="estimatedTime"
          className=" bg-gray-100 cursor-text p-12 pl-50 w-full placeholder-gray-400 rounded-sm h-48 border border-gray-200 text-left"
          placeholder="Enter time (e.g. 3d 4h 30m)"
          onClick={() => setIsSelected(true)}
          autoComplete="off"
          value={showEsimatedTime(estimatedTimeObject!)}
        /> */}
        {/* <TimeHourglassIcon
          wrapperClass="text-gray-400 p-5 absolute top-8 left-10 left-12 border-2 border-gray-400 text-gray-400 border-dotted rounded-full"
          size={18}
        /> */}
      </div>

      <Collapse in={false} style={{ overflow: 'auto' }}>
        {estimatedTimeObject
          ?.filter((item) => item.type === 'member')
          .map((item) => {
            const assignee = assignees.find((obj) => obj.memberId === item.member);
            return (
              <div
                className="flex items-center mb-16 justify-between"
                key={`${item?.member}-${item?.estimate}`}
              >
                <div className="flex items-center">
                  <Avatar src={assignee?.avatar} type="photo" name={assignee?.firstname || ''} />
                  <span className="ml-8 text-sm">{assignee?.firstname}</span>
                </div>
                <div className="font-medium text-sm text-gray-600">
                  {item?.estimate?.text ?? '0 days 0 hours 0 minutes'}
                </div>
              </div>
            );
          })}
      </Collapse>
    </div>
  );
};
